<template>
  <Toast position="top-center" @close="onToastClose">
    <template #icon></template>
  </Toast>
  <App v-if="mainSession?.isConnected"></App>
</template>

<script setup>
import { computed, reactive, ref, watch, watchEffect, onErrorCaptured } from "vue";
import { useToast } from "primevue/usetoast";
import App from "./App.vue";
import store from "./store.js";
import Session, { clearServerErrorToastFlag } from "./Session.js";
import { getBoardHostname } from "./appUtility.js";
import { watchImmediate } from "@vueuse/core";

// Configure error handling.
let toast = useToast();
store.toast = toast;

onErrorCaptured((err, componentInstance, info) => {
  toast.add({
    severity: "error",
    summary: "An error occured",
    detail: err.message
  });
  console.error(err);
  return false;
});

function onToastClose(event) {
  if (event?.message?.context == "server-error")
    clearServerErrorToastFlag();
}

let mainSession = computed(() => {
  return store.sessions.find(session => session.hostname == window.location.host);
});

let hostnames = computed(() => {
  let boards = mainSession.value?.system?.boardsOnSite.children ?? [];
  let boardsFiltered = boards.filter(board =>
    board.programName.value == "mvv2" &&
    board.programVersion.value == `${mainSession.value?.system.programVersionMajor.value}.0.0` &&
    // board.programCommit.value == mainSession.value?.system.programCommit.value &&
    board.online.value &&
    board.showUi.value
  );
  let hostnames = boardsFiltered.map(board => {
    let preferredHostname = getBoardHostname(board);
    let fallbackHostname = getBoardHostname(board, true);
    let session = store.sessions.find(session => session.hostname == preferredHostname || session.hostname == fallbackHostname);
    if (session == null)
      return preferredHostname;
    else
      return (session.didConnectionFail) ? fallbackHostname : session.hostname;
  });
  if (hostnames.length == 0) {
    hostnames.push(window.location.host);
  }
  return hostnames;
});

watchEffect(() => {
  // Close and remove expired sessions.
  let expiredSessionIds = store.sessions.filter(session => session.didSessionExpire);
  if (expiredSessionIds.length) {
    expiredSessionIds.forEach(session => session.close());
    store.sessions = store.sessions.filter(session => !expiredSessionIds.includes(session));
  }

  // Close and remove sessions for expired boards.
  let hostnamesCopy = hostnames.value;
  let expiredSessions = store.sessions.filter(session => !hostnamesCopy.includes(session.hostname));
  if (expiredSessions.length) {
    expiredSessions.forEach(session => session.close());
    store.sessions = store.sessions.filter(session => !expiredSessions.includes(session));
  }
  // Add sessions for new boards.
  let newHostnames = hostnamesCopy.filter(hostname => !store.sessions.some(session => session.hostname == hostname));
  let newSessions = newHostnames.map(hostname => reactive(new Session(hostname)));
  newSessions.forEach(session => session.loadApp(() => session.pollApp(null, 500)));
  store.sessions.push(... newSessions);
});

let targetHostname = computed(() => {
  let system = mainSession.value?.system;
  let board = system?.boardsOnSite.children.find(board => board.macAddress.value == system.macAddr.value);
  let hostname = board ? getBoardHostname(board) : window.location.host;
  return hostname;
});
watch(targetHostname, () => {
  let { pathname, search, hash } = window.location;
  window.location = `http://${targetHostname.value}${pathname}${search}${hash}`;
}, { flush: 'sync', once: true });

// Keep the window title in sync with the site/board name of the target board.
let windowTitle = computed(() => {
  if (mainSession.value?.isConnected)
    return `${mainSession.value.system.siteName.value} - ${mainSession.value.system.boardName.value}`;
  else 
    return "Microvent Vision";
});
watchImmediate(windowTitle, () => {
  document.title = windowTitle.value;
});
</script>